import { Link, useParams } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Reveal } from "@/components/Reveal";
import { ENTERPRISE_SERVICES } from "@/data/content";
import { ArrowRight } from "lucide-react";

export default function Services() {
  return (
    <Layout>
      <section className="pt-36 pb-12 lg:pt-44" data-testid="services-hero">
        <div className="container-editorial">
          <Reveal>
            <div className="eyebrow">Service Portfolio</div>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="font-display text-5xl md:text-7xl mt-6 leading-[1.02] tracking-tight max-w-4xl">
              Strategic Workforce, Talent & <span className="italic text-gold">HR Transformation</span> Services.
            </h1>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-10 max-w-2xl text-lg text-slate2 leading-relaxed">
              A consulting-led HR and workforce solutions practice focused on solving complex talent, workforce, and
              organizational challenges for enterprises, GCCs, startups, and growth-stage organizations.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16">
        <div className="container-editorial space-y-24">
          {ENTERPRISE_SERVICES.map((s, i) => {
            const flip = i % 2 === 1;
            return (
              <Reveal key={s.slug}>
                <article
                  className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center"
                  data-testid={`services-row-${s.slug}`}
                >
                  <div className={`lg:col-span-6 ${flip ? "lg:order-2" : ""}`}>
                    <div className="aspect-[4/3] overflow-hidden border border-[#E0DCD3] bg-ivory-surface">
                      <img src={s.image} alt={s.title} className="w-full h-full object-cover" />
                    </div>
                  </div>
                  <div className={`lg:col-span-6 ${flip ? "lg:order-1" : ""}`}>
                    <div className="eyebrow">{s.kicker}</div>
                    <h2 className="font-display text-3xl md:text-4xl mt-4 leading-tight tracking-tight">{s.title}</h2>
                    <p className="font-display italic text-xl md:text-2xl text-gold mt-3">{s.tagline}</p>
                    <p className="mt-6 text-slate2 leading-relaxed">{s.overview}</p>
                    <div className="mt-8">
                      <Link to={`/services/${s.slug}`} className="btn-ghost" data-testid={`services-link-${s.slug}`}>
                        Read the mandate <ArrowRight size={12} />
                      </Link>
                    </div>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </section>
    </Layout>
  );
}

export function ServiceDetail() {
  const { slug } = useParams();
  const service = ENTERPRISE_SERVICES.find((s) => s.slug === slug);

  if (!service) {
    return (
      <Layout>
        <section className="pt-40 pb-24">
          <div className="container-editorial">
            <div className="eyebrow">404</div>
            <h1 className="font-display text-5xl mt-6">Mandate not found.</h1>
            <Link to="/services" className="btn-outline mt-8">Back to services</Link>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="pt-36 pb-16 lg:pt-44" data-testid={`service-detail-${service.slug}`}>
        <div className="container-editorial">
          <Link to="/services" className="font-mono text-[10px] uppercase tracking-[0.24em] text-slate2 hover:text-gold">
            ← All mandates
          </Link>
          <div className="eyebrow mt-8">{service.kicker}</div>
          <h1 className="font-display text-5xl md:text-6xl mt-4 leading-[1.05] tracking-tight max-w-4xl">
            {service.title}
          </h1>
          <p className="font-display italic text-2xl md:text-3xl text-gold mt-4 max-w-3xl">{service.tagline}</p>
        </div>
      </section>

      <section className="py-12">
        <div className="container-editorial grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="aspect-[16/10] overflow-hidden border border-[#E0DCD3] bg-ivory-surface">
              <img src={service.image} alt={service.title} className="w-full h-full object-cover" />
            </div>

            <div className="mt-10 hairline pt-10">
              <div className="eyebrow">Overview</div>
              <p className="mt-4 text-lg leading-relaxed text-slate2">{service.overview}</p>
            </div>

            <div className="mt-10 hairline pt-10">
              <div className="eyebrow">Core Capabilities</div>
              <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                {service.capabilities.map((o) => (
                  <li key={o} className="flex gap-3 text-slate2">
                    <span className="text-gold">·</span>
                    <span className="text-base">{o}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-10 hairline pt-10">
              <div className="eyebrow">Engagement Approach</div>
              <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                {service.approach.map((o) => (
                  <li key={o} className="flex gap-3 text-slate2">
                    <span className="text-gold">·</span>
                    <span className="text-base">{o}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-10 hairline pt-10">
              <div className="eyebrow">Business Value</div>
              <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                {service.outcomes.map((o) => (
                  <li key={o} className="flex gap-3 text-slate2">
                    <span className="text-gold">·</span>
                    <span className="text-base">{o}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <aside className="lg:col-span-5">
            <div className="panel sticky top-28">
              <div className="eyebrow">Engage the practice</div>
              <p className="mt-3 text-slate2 leading-relaxed">
                Enterprise engagements begin with a private conversation — not a proposal template. Write to Roy
                directly and he will personally respond.
              </p>
              <div className="hairline mt-8 pt-8">
                <Link to="/contact" className="btn-primary w-full" data-testid="service-detail-contact-cta">
                  Open a conversation
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </Layout>
  );
}
